import React, { useState, useMemo } from 'react';
import { Card, CardHeader, CardContent } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { orgStructure, getRiskProfile, identifyGaps, RISK_LEVELS } from '../data/digitalTwinLogic';

export const DigitalTwin = () => {
  const [selectedNode, setSelectedNode] = useState(orgStructure);
  const [history, setHistory] = useState([]);

  const risk = useMemo(() => getRiskProfile(selectedNode.metrics.we), [selectedNode]);
  const gaps = useMemo(() => identifyGaps(selectedNode), [selectedNode]);

  const navigateTo = (node) => {
    setHistory([...history, selectedNode]);
    setSelectedNode(node);
  };

  const goBack = () => {
    const prev = history.pop();
    if (prev) {
      setHistory([...history]);
      setSelectedNode(prev);
    }
  };

  return (
    <div className="space-y-8 pb-20">
      <div className="flex justify-between items-end">
        <div>
          <h1 className="text-2xl font-black text-slate-900 uppercase tracking-tight">Organizational Digital Twin</h1>
          <p className="text-slate-500 mt-1">Virtuell speiling av organisasjonsstrukturen og dens helsetilstand.</p>
        </div>
        <div className="flex gap-2">
          {history.length > 0 && (
            <Button variant="secondary" onClick={goBack} icon={<SafeIcon name="ArrowLeft" />}>Opp et nivå</Button>
          )}
          <Button variant="primary" icon={<SafeIcon name="Cpu" />}>Kjør Simulering</Button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
        {/* Navigation & Structure View */}
        <div className="lg:col-span-3 space-y-6">
          <Card className="border-none shadow-xl bg-slate-900 text-white overflow-hidden min-h-[500px] flex flex-col">
            <div className="p-8 border-b border-white/5 flex justify-between items-center">
              <div>
                <span className="text-[10px] font-black text-indigo-400 uppercase tracking-widest">{selectedNode.type}</span>
                <h2 className="text-3xl font-black tracking-tighter uppercase mt-1">{selectedNode.name}</h2>
              </div>
              <div className="text-right">
                <p className="text-[10px] font-bold text-slate-500 uppercase">Manager</p>
                <p className="text-sm font-bold text-indigo-300">{selectedNode.manager}</p>
              </div>
            </div>

            <CardContent className="flex-1 p-8">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                <AnimatePresence mode="popLayout">
                  {selectedNode.children ? selectedNode.children.map((child) => {
                    const childRisk = getRiskProfile(child.metrics.we);
                    return (
                      <motion.div
                        key={child.id}
                        layout
                        initial={{ opacity: 0, scale: 0.9 }}
                        animate={{ opacity: 1, scale: 1 }}
                        exit={{ opacity: 0, scale: 0.9 }}
                        onClick={() => navigateTo(child)}
                        className={`group cursor-pointer p-6 rounded-[32px] border-2 border-transparent hover:border-${childRisk.color}-500/50 bg-white/5 transition-all hover:bg-white/10`}
                      >
                        <div className="flex justify-between items-start mb-6">
                          <div className={`w-10 h-10 rounded-2xl bg-${childRisk.color}-500/20 text-${childRisk.color}-400 flex items-center justify-center`}>
                            <SafeIcon name={child.type === 'Department' ? 'Layers' : 'Users'} />
                          </div>
                          <div className={`px-2 py-1 rounded-lg text-[10px] font-black bg-${childRisk.color}-500 text-white uppercase`}>
                            {child.metrics.we}%
                          </div>
                        </div>
                        <h4 className="text-white font-bold text-lg mb-1">{child.name}</h4>
                        <p className="text-[10px] text-slate-500 font-bold uppercase mb-4">{child.manager}</p>
                        
                        <div className="flex justify-between text-[10px] font-black text-slate-400">
                          <span>STRESS</span>
                          <span className={child.metrics.stress > 70 ? 'text-rose-400' : ''}>{child.metrics.stress}%</span>
                        </div>
                        <div className="mt-1 w-full bg-white/5 h-1 rounded-full overflow-hidden">
                          <div className={`h-full bg-${childRisk.color}-500`} style={{ width: `${child.metrics.we}%` }} />
                        </div>
                      </motion.div>
                    );
                  }) : (
                    <div className="col-span-full py-20 text-center space-y-4">
                      <div className="w-20 h-20 bg-white/5 rounded-full flex items-center justify-center mx-auto border border-white/10">
                        <SafeIcon name="User" className="text-4xl text-slate-500" />
                      </div>
                      <h3 className="text-xl font-bold">Teammedlemmer ({selectedNode.employees})</h3>
                      <p className="text-slate-500 text-sm max-w-xs mx-auto">Digitale tvillinger for individuelle ansatte er aktive og overvåkes for belastningsmønstre.</p>
                      <Button variant="secondary" className="bg-white/5 border-white/10 text-white">Se ansatt-matrise</Button>
                    </div>
                  )}
                </AnimatePresence>
              </div>
            </CardContent>

            <div className="p-4 bg-white/5 border-t border-white/5 flex justify-center gap-8">
              {Object.values(RISK_LEVELS).map(l => (
                <div key={l.label} className="flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full`} style={{ backgroundColor: l.code }} />
                  <span className="text-[10px] font-black text-slate-500 uppercase tracking-widest">{l.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        {/* Detail Panel */}
        <div className="space-y-6">
          <Card className={`border-t-4 border-t-${risk.color}-500`}>
            <CardHeader title="Health Metrics" subtitle="Real-time twin status" />
            <CardContent className="space-y-6">
              {[
                { label: 'Work Environment Score', key: 'we', color: 'indigo' },
                { label: 'Stress Index', key: 'stress', color: 'rose' },
                { label: 'Psychological Safety', key: 'safety', color: 'emerald' },
                { label: 'Conflict Risk', key: 'conflict', color: 'orange' }
              ].map(m => (
                <div key={m.key} className="space-y-1">
                  <div className="flex justify-between text-[10px] font-black text-slate-400 uppercase">
                    <span>{m.label}</span>
                    <span className="text-slate-900">{selectedNode.metrics[m.key]}%</span>
                  </div>
                  <div className="w-full bg-slate-100 h-1.5 rounded-full overflow-hidden">
                    <div className={`h-full bg-${m.color}-500 transition-all duration-1000`} style={{ width: `${selectedNode.metrics[m.key]}%` }} />
                  </div>
                </div>
              ))}
            </CardContent>
          </Card>

          <Card className="bg-slate-50 border-slate-200">
            <CardHeader title="AI Insights" icon={<SafeIcon name="Zap" className="text-indigo-600" />} />
            <CardContent className="space-y-4">
              {gaps.length > 0 ? gaps.map((gap, i) => (
                <div key={i} className="flex gap-3 p-4 bg-white rounded-2xl border border-slate-100 shadow-sm">
                  <SafeIcon name="AlertTriangle" className="text-rose-500 shrink-0 mt-0.5" />
                  <p className="text-xs font-bold text-slate-700 leading-relaxed">{gap}</p>
                </div>
              )) : (
                <div className="text-center py-4">
                  <SafeIcon name="CheckCircle" className="text-3xl text-emerald-500 mb-2 mx-auto" />
                  <p className="text-xs font-bold text-emerald-700 uppercase">Ingen kritiske avvik</p>
                </div>
              )}
            </CardContent>
          </Card>

          <div className="bg-indigo-600 p-8 rounded-[40px] text-white shadow-xl shadow-indigo-100 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-32 h-32 bg-white/10 rounded-full -mr-16 -mt-16"></div>
            <h4 className="text-xl font-black mb-2 uppercase tracking-tighter">Manager Responsibility</h4>
            <p className="text-xs text-indigo-100 leading-relaxed mb-6">
              Som manager for {selectedNode.name} er {selectedNode.manager} ansvarlig for å følge opp avvik innen 48 timer.
            </p>
            <Button variant="secondary" className="w-full bg-white text-indigo-600 border-none">Send Lederstøtte-pakke</Button>
          </div>
        </div>
      </div>
    </div>
  );
};